import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactFlow, { MiniMap, Controls, Background } from 'reactflow';
import 'reactflow/dist/style.css';

const sectionSpacingX = 260;
const itemSpacingY = 110;
const sectionStartY = 170;

const baseNodeStyle = {
  borderRadius: 12,
  padding: '12px 16px',
  border: '1px solid #333',
  background: '#1f1f23',
  color: '#fff',
  fontSize: 12,
  width: 220,
};

const sectionNodeStyle = {
  ...baseNodeStyle,
  background: '#2b2b31',
  border: '1px solid #444',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const rootNodeStyle = {
  ...baseNodeStyle,
  background: '#b91c1c',
  border: '1px solid #f87171',
  fontWeight: 700,
  fontSize: 14,
  textAlign: 'center',
};

const createEntry = (label, value, extra = '') => {
  if (value === undefined || value === null || value === '') return null;
  return { label, value: String(value), extra };
};

const buildMindMapData = (moment) => {
  if (!moment) {
    return { nodes: [], edges: [] };
  }

  const content = moment.content?.technical || {};
  const requiredFlag =
    typeof content.interaction?.required === 'boolean'
      ? content.interaction.required
      : null;

  let requiredDisplay;
  if (requiredFlag !== null) {
    requiredDisplay = requiredFlag ? 'Yes' : 'No';
  }

  const sections = [
    {
      key: 'general',
      label: 'General',
      entries: [
        createEntry('Name', content.general?.name),
        createEntry('Description', content.general?.description),
        createEntry('Notes', content.notes),
      ],
    },
    {
      key: 'interaction',
      label: 'Interaction',
      entries: [
        createEntry('Touch Query', content.interaction?.touchObjectQuery),
        createEntry('Duration (s)', content.interaction?.duration),
        createEntry('Required', requiredDisplay),
      ],
    },
    {
      key: 'beforeTouch',
      label: 'Before Touch',
      entries: [
        createEntry('Voice Over', content.beforeTouch?.voiceOver),
        createEntry('Display Text', content.beforeTouch?.displayText),
        createEntry('Objects Query', content.beforeTouch?.objects?.query),
        createEntry('Image', content.beforeTouch?.image),
        createEntry('Video', content.beforeTouch?.video),
      ],
    },
    {
      key: 'afterTouch',
      label: 'After Touch',
      entries: [
        createEntry('Voice Over', content.afterTouch?.voiceOver),
        createEntry('Display Text', content.afterTouch?.displayText),
        createEntry('Visual Effect', content.afterTouch?.visualEffect),
        createEntry('SFX', content.afterTouch?.sfx),
        createEntry('Meta Layer', content.afterTouch?.metaLayer),
        createEntry('Objects Query', content.afterTouch?.objects?.query),
        createEntry('Image', content.afterTouch?.image),
        createEntry('Video', content.afterTouch?.video),
      ],
    },
    {
      key: 'voiceWarnings',
      label: 'Voice Warnings',
      entries:
        content.voiceWarnings?.map((warning, index) =>
          createEntry(
            `Warning ${index + 1}`,
            warning.message || warning.text,
            warning.touchObjectQuery
              ? `Trigger: ${warning.touchObjectQuery}`
              : ''
          )
        ) || [],
    },
  ];

  const filteredSections = sections.map((section) => ({
    ...section,
    entries: section.entries.filter(Boolean),
  }));

  const availableSections = filteredSections.filter(
    (section) => section.entries.length > 0
  );

  if (availableSections.length === 0) {
    return { nodes: [], edges: [] };
  }

  const nodes = [];
  const edges = [];

  const rootX =
    ((availableSections.length - 1) * sectionSpacingX) / 2 -
    sectionSpacingX / 2;

  nodes.push({
    id: `root-${moment.id}`,
    data: { label: content.general?.name || '[TECH] Moment Overview' },
    position: { x: rootX, y: 0 },
    style: rootNodeStyle,
  });

  for (
    let sectionIndex = 0;
    sectionIndex < availableSections.length;
    sectionIndex++
  ) {
    const section = availableSections[sectionIndex];
    const sectionId = `section-${section.key}`;
    const sectionX = sectionIndex * sectionSpacingX;

    nodes.push({
      id: sectionId,
      data: { label: section.label },
      position: { x: sectionX, y: sectionStartY },
      style: sectionNodeStyle,
    });

    edges.push({
      id: `edge-root-${sectionId}`,
      source: `root-${moment.id}`,
      target: sectionId,
      type: 'smoothstep',
      style: { stroke: '#f87171', strokeWidth: 2 },
    });

    for (
      let entryIndex = 0;
      entryIndex < section.entries.length;
      entryIndex++
    ) {
      const entry = section.entries[entryIndex];
      if (!entry) continue;
      const entryId = `${sectionId}-entry-${entryIndex}`;
      const entryY = sectionStartY + 100 + entryIndex * itemSpacingY;

      nodes.push({
        id: entryId,
        data: {
          label: (
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-400">
                {entry.label}
              </div>
              <div className="text-white font-medium break-words">
                {entry.value}
              </div>
              {entry.extra && (
                <div className="text-[10px] text-gray-400 mt-1">
                  {entry.extra}
                </div>
              )}
            </div>
          ),
        },
        position: { x: sectionX, y: entryY },
        style: baseNodeStyle,
      });

      edges.push({
        id: `edge-${sectionId}-${entryId}`,
        source: sectionId,
        target: entryId,
        type: 'smoothstep',
        style: { stroke: '#4b5563', strokeWidth: 1.5 },
      });
    }
  }

  return { nodes, edges };
};

export default function MomentMindMap({ moment }) {
  const { nodes, edges } = useMemo(() => buildMindMapData(moment), [moment]);

  if (!moment) {
    return (
      <div className="w-full h-96 bg-zinc-900 rounded-lg flex items-center justify-center text-gray-400">
        Select a moment to visualize the technical structure.
      </div>
    );
  }

  if (nodes.length === 0) {
    return (
      <div className="w-full h-96 bg-zinc-900 rounded-lg flex items-center justify-center text-gray-400">
        No technical data available to build a mindmap.
      </div>
    );
  }

  return (
    <div className="w-full h-[520px] bg-zinc-900 rounded-lg border border-zinc-800 overflow-hidden">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        zoomOnScroll
        panOnDrag
        zoomOnPinch
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#2B2C2F" gap={16} />
        <Controls className="bg-[#1A1B1E] border border-[#37383F] rounded-lg" />
        {/* <MiniMap
          className="bg-[#1A1B1E] border border-[#37383F] rounded-lg"
          nodeColor={() => '#b91c1c'}
        /> */}
      </ReactFlow>
    </div>
  );
}

MomentMindMap.propTypes = {
  moment: PropTypes.shape({
    id: PropTypes.string,
    content: PropTypes.object,
  }),
};
